import { Link } from 'react-router-dom'
import { LoadingScreen } from '@/components/LoadingScreen'
import { useFantasyChallenges, type FantasyChallenge } from '@/features/fantasy-challenges/api'
import { useTranslation } from '@/i18n'

function formatDeadline(value: string | null | undefined) {
  if (!value) return 'Geen deadline ingesteld'
  return new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(value))
}

export function FantasyChallengesPage() {
  const { t } = useTranslation()
  const challengesQuery = useFantasyChallenges()

  if (challengesQuery.isLoading) {
    return <LoadingScreen compact />
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-7">
      <header className="flex flex-col gap-4 rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-6 sm:flex-row sm:items-end sm:justify-between sm:p-8">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-accent-200">{t('Golero Manager')}</p>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-white">{t('Challenges')}</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-surface-400">
            {t('Bouw een team binnen een bijzondere uitdaging en verdien punten met je selectie.')}
          </p>
        </div>
        <Link to="/dashboard/manager/challenges/aanmaken" className="inline-flex shrink-0 items-center justify-center rounded-xl bg-accent-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-accent-500">
          {t('Nieuwe challenge')}
        </Link>
      </header>

      {challengesQuery.isError ? (
        <section className="rounded-[1.5rem] border border-red-300/15 bg-red-300/[0.04] p-6 text-sm text-red-200">
          {t('Challenges konden niet worden geladen. Probeer het opnieuw.')}
        </section>
      ) : null}

      {!challengesQuery.isError && challengesQuery.data?.length === 0 ? (
        <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-8 text-center">
          <p className="text-base font-bold text-white">{t('Er zijn nog geen actieve challenges.')}</p>
          <p className="mt-2 text-sm text-surface-400">{t('Nieuwe challenges verschijnen hier zodra ze beschikbaar zijn.')}</p>
        </section>
      ) : null}

      {challengesQuery.data && challengesQuery.data.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {challengesQuery.data.map((challenge) => <ChallengeCard key={challenge.id} challenge={challenge} />)}
        </div>
      ) : null}
    </div>
  )
}

function ChallengeCard({ challenge }: { challenge: FantasyChallenge }) {
  const { t } = useTranslation()
  const icon = challenge.icons?.[0]
  const isOpen = challenge.status === 'open'

  return (
    <Link
      to={`/dashboard/manager/challenges/${challenge.id}`}
      className="group flex min-h-56 flex-col rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 transition hover:-translate-y-0.5 hover:border-accent-300/30 hover:bg-[#111719]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/[0.08] bg-[#172024] p-2">
          {icon?.url ?? icon?.image_path ? <img src={icon.url ?? icon.image_path ?? undefined} alt="" className="h-full w-full object-contain" /> : <span className="text-xl" aria-hidden="true">⚽</span>}
        </div>
        <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${isOpen ? 'bg-emerald-400/10 text-emerald-300' : 'bg-white/[0.07] text-surface-400'}`}>
          {isOpen ? t('Open') : challenge.status === 'completed' ? t('Afgerond') : t('Scoring')}
        </span>
      </div>
      <h2 className="mt-5 text-lg font-black tracking-tight text-white group-hover:text-accent-100">{challenge.title}</h2>
      {challenge.description ? <p className="mt-2 line-clamp-2 text-sm leading-6 text-surface-400">{challenge.description}</p> : null}
      <div className="mt-auto flex items-end justify-between gap-3 pt-5 text-xs text-surface-500">
        <span>{challenge.scope_type === 'international' ? t('Internationaal') : t('Competitie')}</span>
        <span className="text-right">{formatDeadline(challenge.submission_deadline_at)}</span>
      </div>
    </Link>
  )
}
